/**
 * Wisetree Wallet — frontend client. Reads the single-currency balance (WP, Wisetree Points) and
 * the one top-up ladder. Spend happens server-side inside the capability paths; top-ups are
 * credited server-side after payment — so there's no client "grant credits" call here.
 *
 * WP pays for everything: agent thinking, media generation, file import/export. It replaced the
 * old SP (thinking) + MP (media) split, which let a creator hold credits they could not spend on
 * the thing they were blocked on. "Wisetree Points" is spelled out on wallet surfaces, `1,200 WP`
 * is the unit, and "credits" stays the ordinary word inside a sentence.
 */
import { callFunction } from '../utils/apiClient';

export type Currency = 'WP';

/** The one currency, as a value (specs, labels, payloads). */
export const WP: Currency = 'WP';

/** The retired two-currency codes. Kept ONLY to LABEL ledger rows written before the merge — a
 *  pre-merge MP row must keep reading as MP, never be restated as WP (that would halve its value). */
export type LegacyCurrency = 'SP' | 'MP';

export interface Wallet {
  user_id: string;
  /** The one spendable balance, in WP. */
  wp: number;
  plan: 'free' | 'pro' | 'enterprise';
  last_monthly_at: string | null;
  /** Next Stripe renewal/charge date (ISO), from Stripe — the accurate date shown on the wallet. */
  renews_at: string | null;
  wp_spent: number;
  wp_topped: number;
}

export interface TopUpPack {
  id: string;
  price_usd: number;
  credits: number;
  bonus_pct: number;
  tag?: string;
  /** A retired pack, resolvable for an in-flight checkout — never offered for sale. */
  legacy?: true;
  /** The starter pack: sold only to an account that has never bought a pack (the server decides). */
  first_purchase_only?: true;
}

/** Capability → { kind, cost } map. Used for internal sizing/confirm math, never as an ambient price tag. */
export type CapabilityCosts = Record<string, { kind: 'text' | 'media'; cost: number }>;

/** Current balance, the capability cost model, the plan's WP balance cap (the usage-bar denominator), and the
 *  first-purchase starter pack with whether this account may still buy it (decided server-side). */
export const getWallet = () =>
  callFunction<{ wallet: Wallet; capabilities: CapabilityCosts; cap?: number; starterPack?: TopUpPack; starterPackEligible?: boolean }>('getWallet', {});

export interface StorageStatus {
  /** Internal accounting unit — never rendered; use the MB figures for anything a creator reads. */
  usedHp: number; capHp: number;
  /** What a creator is shown: plain megabytes used, out of the plan's megabyte allowance. */
  usedMb: number; capMb: number;
  plan: 'free' | 'pro' | 'enterprise';
}
/** The user's storage usage vs their plan cap, in plain MB (for the usage section + upgrade prompt). */
export const getStorageStatus = () =>
  callFunction<StorageStatus>('getStorageStatus', {});

/** The one top-up ladder, the active promo bonus (0 when no promo is running), and the first-purchase starter
 *  pack with whether this account may buy it. The pack's definition always comes back (so a finished starter
 *  checkout can still be priced); only `starterPackEligible` decides whether it is offered. */
export const getTopUpInfo = () =>
  callFunction<{ packs: TopUpPack[]; promoBonus: number; starterPack?: TopUpPack; starterPackEligible?: boolean }>('getTopUpPacks', {})
    .then((r) => ({
      packs: r.packs || [], promoBonus: r.promoBonus || 0,
      starterPack: r.starterPack ?? null, starterPackEligible: r.starterPackEligible === true,
    }));

/** Flat list of the packs on sale. */
export const getTopUpPacks = () => getTopUpInfo().then((r) => r.packs);

/** Who triggered a ledger entry — the user themselves, or a proactive agent acting on its own. */
export interface LedgerActor {
  type: 'user' | 'agent' | 'system';
  name: string;
  /** The chat/group it happened in. */
  context: string | null;
}

/** One wallet transaction row (grant / charge / top-up), normalized for the history view. */
export interface LedgerEntry {
  id: string;
  type: 'welcome' | 'grant' | 'charge' | 'topup' | 'refund' | string;
  /** The currency the row was WRITTEN in — WP today, SP/MP for pre-merge rows. Displayed as stored. */
  currency: Currency | LegacyCurrency | null;
  capability: string | null;
  pack: string | null;
  wp: number | null;
  /** Pre-merge grant amounts, still shown in the currency they were granted in. */
  sp: number | null;
  mp: number | null;
  amount: number | null;
  balance_after: number | null;
  /** Who actioned this (user vs proactive agent) + the chat it happened in; null for legacy rows. */
  actor: LedgerActor | null;
  created_at: string | null;
}

/** Recent wallet transactions, newest first. Optional [sinceMs, untilMs) window; 1000-row cap. */
export const getWalletLedger = (opts: { limit?: number; sinceMs?: number; untilMs?: number } = {}) =>
  callFunction<{ entries: LedgerEntry[] }>('getWalletLedger', {
    limit: opts.limit ?? 50, sinceMs: opts.sinceMs, untilMs: opts.untilMs,
  }).then((r) => r.entries || []);

/** Redeem an alphanumeric promo code → grants WP (throws with a friendly message on failure). */
export const redeemPromoCode = (code: string) =>
  callFunction<{ code: string; wp: number; label: string }>('redeemPromoCode', { code });

/** ADMIN ONLY — current month's promo code + redemption count (for distribution). */
export const getActivePromoCode = () =>
  callFunction<{ code: string; reward: { wp: number }; redeemed: number; cap: number | null }>('getActivePromoCode', {});
